import { useEffect, useRef, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
    loadPOSData,
    refreshProducts,
    processOrder,
    confirmMarkDelivered,
    setProductSearch,
    clearMessages,
    setError,
    setSuccess,
    addToCart,
    updateCartQuantity,
    updateCartItemPrice,
    removeFromCart,
    clearCart,
    setSelectedCustomer,
    addCustomer,
    setDeliveryCharge,
    setDiscount,
    openPaymentDialog,
    closePaymentDialog,
    openQuantityDialog,
    closeQuantityDialog,
    setQuantityValue,
    setQuantityDialogIsGift,
    openQuickProductDialog,
    closeQuickProductDialog,
    openReceiptModal,
    closeReceiptModal,
    skipDeliveryConfirm,
    addProduct,
    initializeFromStorage,
    resetPOSState,
    selectPOSState,
    selectCart,
    selectCartTotal,
} from "../../../store/slices/posSlice";
import { Product, Customer, Order } from "../../../types";
import { POSPayment } from "../types";

// Local Storage Key
const POS_STORAGE_KEY = "catelyn-pos-state";

// Hook for POS State Management
export const usePOSRedux = () => {
    const dispatch = useAppDispatch();
    const posState = useAppSelector(selectPOSState);
    const cart = useAppSelector(selectCart);
    const cartTotal = useAppSelector(selectCartTotal);

    const searchInputRef = useRef<HTMLInputElement>(null);

    // Initialize POS data and restore from localStorage
    const initializePOS = useCallback(async () => {
        try {
            // Load saved state from localStorage
            const savedState = localStorage.getItem(POS_STORAGE_KEY);
            if (savedState) {
                const parsedState = JSON.parse(savedState);
                dispatch(initializeFromStorage(parsedState));
            }

            // Load fresh data from API
            await dispatch(loadPOSData()).unwrap();
        } catch (error) {
            console.error("Failed to initialize POS:", error);
        }
    }, [dispatch]);

    // Save state to localStorage whenever relevant state changes
    useEffect(() => {
        if (posState.isInitialized) {
            const stateToSave = {
                cart: posState.cart,
                selectedCustomer: posState.selectedCustomer,
                deliveryCharge: posState.deliveryCharge,
                discount: posState.discount,
                productSearch: posState.productSearch,
                lastCompletedOrder: posState.lastCompletedOrder,
                lastPaymentMethod: posState.lastPaymentMethod,
                lastAmountPaid: posState.lastAmountPaid,
                lastReceiptCustomer: posState.lastReceiptCustomer,
            };

            localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(stateToSave));
        }
    }, [
        posState.cart,
        posState.selectedCustomer,
        posState.deliveryCharge,
        posState.discount,
        posState.productSearch,
        posState.lastCompletedOrder,
        posState.lastPaymentMethod,
        posState.lastAmountPaid,
        posState.lastReceiptCustomer,
        posState.isInitialized,
    ]);

    // Initialize on mount
    useEffect(() => {
        if (!posState.isInitialized) {
            initializePOS();
        }
    }, [initializePOS, posState.isInitialized]);

    // Focus search bar utility
    const focusSearchBar = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
            dispatch(setProductSearch(""));
        }
    }, [dispatch]);

    // Product search handler
    const handleProductSearch = useCallback(
        (term: string) => {
            dispatch(setProductSearch(term));
        },
        [dispatch],
    );

    // Cart management
    const handleAddToCart = useCallback(
        (product: Product, quantity: number, isGift: boolean = false) => {
            dispatch(addToCart({ product, quantity, isGift }));
            return true;
        },
        [dispatch],
    );

    const handleUpdateCartQuantity = useCallback(
        (productId: string, quantity: number) => {
            dispatch(updateCartQuantity({ productId, quantity }));
        },
        [dispatch],
    );

    const handleUpdateCartItemPrice = useCallback(
        (productId: string, unitPrice: number) => {
            dispatch(updateCartItemPrice({ productId, unitPrice }));
        },
        [dispatch],
    );

    const handleRemoveFromCart = useCallback(
        (productId: string) => {
            dispatch(removeFromCart(productId));
        },
        [dispatch],
    );

    const handleClearCart = useCallback(() => {
        dispatch(clearCart());
    }, [dispatch]);

    // Customer management
    const handleSetSelectedCustomer = useCallback(
        (customer: Customer | null) => {
            dispatch(setSelectedCustomer(customer));
        },
        [dispatch],
    );

    const handleAddCustomer = useCallback(
        (customer: Customer) => {
            dispatch(addCustomer(customer));
            dispatch(setSelectedCustomer(customer));
            dispatch(
                setSuccess(
                    `Customer "${customer.first_name} ${customer.last_name}" added successfully!`,
                ),
            );
        },
        [dispatch],
    );

    // Transaction management
    const handleSetDeliveryCharge = useCallback(
        (charge: number) => {
            dispatch(setDeliveryCharge(charge));
        },
        [dispatch],
    );

    const handleSetDiscount = useCallback(
        (discount: number) => {
            dispatch(setDiscount(discount));
        },
        [dispatch],
    );

    // Order processing
    const handleProcessOrder = useCallback(
        async (
            payments: POSPayment[],
            allowNegativeStock: boolean = false,
        ) => {
            try {
                await dispatch(
                    processOrder({
                        payments,
                        cart: posState.cart,
                        selectedCustomer: posState.selectedCustomer,
                        deliveryCharge: posState.deliveryCharge,
                        discount: posState.discount,
                        allowNegativeStock,
                    }),
                ).unwrap();

                return true;
            } catch (error) {
                return false;
            }
        },
        [
            dispatch,
            posState.cart,
            posState.selectedCustomer,
            posState.deliveryCharge,
            posState.discount,
        ],
    );

    // Delivery confirmation (shown right after a sale is created PENDING)
    const handleConfirmMarkDelivered = useCallback(
        async (orderId: string) => {
            try {
                const freshOrder = await dispatch(
                    confirmMarkDelivered(orderId),
                ).unwrap();
                return freshOrder as Order;
            } catch (error) {
                return null;
            }
        },
        [dispatch],
    );

    const handleSkipDeliveryConfirm = useCallback(() => {
        dispatch(skipDeliveryConfirm());
    }, [dispatch]);

    // Dialog management
    const handleOpenPaymentDialog = useCallback(() => {
        if (cart.length === 0) {
            dispatch(setError("Cart is empty"));
            return;
        }
        dispatch(openPaymentDialog());
    }, [dispatch, cart.length]);

    const handleClosePaymentDialog = useCallback(() => {
        dispatch(closePaymentDialog());
    }, [dispatch]);

    const handleOpenQuantityDialog = useCallback(
        (product: Product, quantity = 1, isGift = false) => {
            dispatch(openQuantityDialog({ product, quantity, isGift }));
        },
        [dispatch],
    );

    const handleCloseQuantityDialog = useCallback(() => {
        dispatch(closeQuantityDialog());
        // Back to the search box, as after adding an item (keyboard flow).
        focusSearchBar();
    }, [dispatch, focusSearchBar]);

    const handleSetQuantityValue = useCallback(
        (quantity: number) => {
            dispatch(setQuantityValue(quantity));
        },
        [dispatch],
    );

    const handleSetQuantityDialogIsGift = useCallback(
        (isGift: boolean) => {
            dispatch(setQuantityDialogIsGift(isGift));
        },
        [dispatch],
    );

    const handleConfirmQuantity = useCallback(() => {
        if (posState.selectedProductForQuantity && posState.quantityValue > 0) {
            const success = handleAddToCart(
                posState.selectedProductForQuantity,
                posState.quantityValue,
                posState.quantityDialogIsGift,
            );
            if (success) {
                dispatch(closeQuantityDialog());
                focusSearchBar();
            }
        }
    }, [
        posState.selectedProductForQuantity,
        posState.quantityValue,
        posState.quantityDialogIsGift,
        handleAddToCart,
        dispatch,
        focusSearchBar,
    ]);

    const handleOpenQuickProductDialog = useCallback(() => {
        dispatch(openQuickProductDialog());
    }, [dispatch]);

    const handleCloseQuickProductDialog = useCallback(() => {
        dispatch(closeQuickProductDialog());
    }, [dispatch]);

    const handleOpenReceiptModal = useCallback(
        (customer: Customer | null = null, order: Order | null = null) => {
            dispatch(
                openReceiptModal({
                    customer: customer || posState.receiptCustomer,
                    order: order || posState.receiptOrder,
                }),
            );
        },
        [dispatch, posState.receiptCustomer, posState.receiptOrder],
    );

    const handleCloseReceiptModal = useCallback(() => {
        dispatch(closeReceiptModal());
    }, [dispatch]);

    // Product management
    const handleAddProduct = useCallback(
        (product: Product) => {
            dispatch(addProduct(product));
            dispatch(
                setSuccess(
                    `Product "${product.name}" added with ${product.stock?.quantity || 0} units available and ready for sale!`,
                ),
            );

            // Auto-open quantity dialog for new product
            handleOpenQuantityDialog(product, 1);
        },
        [dispatch, handleOpenQuantityDialog],
    );

    // Utility functions
    const handleRefreshProducts = useCallback(() => {
        dispatch(refreshProducts());
    }, [dispatch]);

    const handleClearMessages = useCallback(() => {
        dispatch(clearMessages());
    }, [dispatch]);

    const handleResetPOS = useCallback(() => {
        dispatch(resetPOSState());
        localStorage.removeItem(POS_STORAGE_KEY);
    }, [dispatch]);

    // Show last receipt
    const handleShowLastReceipt = useCallback(() => {
        if (posState.lastCompletedOrder) {
            handleOpenReceiptModal(
                posState.lastReceiptCustomer,
                posState.lastCompletedOrder,
            );
        }
    }, [
        posState.lastCompletedOrder,
        posState.lastReceiptCustomer,
        handleOpenReceiptModal,
    ]);

    return {
        // State
        state: posState,
        cart,
        cartTotal,
        searchInputRef,

        // Data management
        initializePOS,
        handleRefreshProducts,

        // UI management
        focusSearchBar,
        handleProductSearch,
        handleClearMessages,
        handleResetPOS,

        // Cart management
        handleAddToCart,
        handleUpdateCartQuantity,
        handleUpdateCartItemPrice,
        handleRemoveFromCart,
        handleClearCart,

        // Customer management
        handleSetSelectedCustomer,
        handleAddCustomer,

        // Transaction management
        handleSetDeliveryCharge,
        handleSetDiscount,
        handleProcessOrder,
        handleConfirmMarkDelivered,
        handleSkipDeliveryConfirm,

        // Dialog management
        handleOpenPaymentDialog,
        handleClosePaymentDialog,
        handleOpenQuantityDialog,
        handleCloseQuantityDialog,
        handleSetQuantityValue,
        handleSetQuantityDialogIsGift,
        handleConfirmQuantity,
        handleOpenQuickProductDialog,
        handleCloseQuickProductDialog,
        handleOpenReceiptModal,
        handleCloseReceiptModal,

        // Product management
        handleAddProduct,

        // Receipt management
        handleShowLastReceipt,
    };
};

// Hook for filtered products (for search functionality)
export const useFilteredProducts = () => {
    const { products, productSearch } = useAppSelector(selectPOSState);

    const filteredProducts = useMemo(() => {
        const tokens = productSearch
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean);

        if (tokens.length === 0) return products;

        return products.filter((product) => {
            const searchableFields = [
                product.name,
                product.sku,
                product.barcode,
                product.category?.name,
                product.brand?.name,
                String(product.selling_price),
            ]
                .filter(Boolean)
                .map((f) => f!.toLowerCase());

            // Every token must match at least one field
            return tokens.every((token) =>
                searchableFields.some((field) => field.includes(token))
            );
        });
    }, [products, productSearch]);

    return {
        products,
        filteredProducts,
        searchTerm: productSearch,
    };
};
